import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, collection, query, where, onSnapshot, serverTimestamp, deleteDoc, addDoc, deleteField, orderBy } from 'firebase/firestore';
import { turkeyCities } from '../data/turkeyCities';
import Logo from '../components/Logo';

function AdminDashboard() {
    const navigate = useNavigate();
    const sessionData = JSON.parse(localStorage.getItem('sahalive_session'));
    const user = JSON.parse(localStorage.getItem('admin')) || JSON.parse(localStorage.getItem('user')) || sessionData?.user;
    const userId = user?._id || user?.id || sessionData?.userId;

    const [activeTab, setActiveTab] = useState('ayarlar');
    const [finPeriod, setFinPeriod] = useState('gun'); // 'gun' | 'ay' | 'yil'
    const [facility, setFacility] = useState({
        name: "", // Tesis Adı (fieldName olarak kaydedilecek)
        ownerFirstName: "", // Sahib Adı
        ownerLastName: "", // Sahib Soyadı
        phone: "", // Telefon
        city: "", 
        district: "",
        openTime: "09:00", 
        closeTime: "24:00",
        depositAmount: 1500,
        imageUrl: "",
        amenities: {
            dus: false,
            otopark: false,
            kantin: false,
            krampon: false,
            eldiven: false,
            servis: false
        }
    });

    const [customerName, setCustomerName] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [reservations, setReservations] = useState([]);
    const [dateList, setDateList] = useState([]);

    // --- CANLI YAYIN + MAÇ KAYITLARI STATE ---
    const [liveUrl, setLiveUrl] = useState("");
    const [liveTestMode, setLiveTestMode] = useState(false);
    const [liveLoaded, setLiveLoaded] = useState(false);
    const [macKayitlari, setMacKayitlari] = useState([]);
    const [yeniKayit, setYeniKayit] = useState({ date: "", timeSlot: "", score: "", duration: "60 dk", videoUrl: "" });
    const hlsRef = useRef(null);
    const videoTestRef = useRef(null);

    const generateDateList = () => {
        const dates = [];
        for (let i = 0; i < 10; i++) {
            const d = new Date();
            d.setDate(d.getDate() + i);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            dates.push({
                fullDate: d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }),
                dayName: d.toLocaleDateString('tr-TR', { weekday: 'long' }),
                value: `${year}-${month}-${day}`
            });
        }
        return dates;
    };

    const generateDynamicHours = (f) => {
        if (!f) return [];
        const start = parseInt((f.openTime || "09:00").split(':')[0]);
        let end = parseInt((f.closeTime || "24:00").split(':')[0]);
        if (end <= start) end += 24;
        const ranges = [];
        for (let i = start; i < end; i++) {
            const s = i % 24 < 10 ? `0${i % 24}` : i % 24;
            const e = (i + 1) % 24 < 10 ? `0${(i + 1) % 24}` : (i + 1) % 24;
            ranges.push(`${s}:00 - ${e}:00`);
        }
        return ranges;
    };

    useEffect(() => {
        if (!userId) {
            navigate('/');
            return;
        }

        const qRes = query(collection(db, "rezervasyonlar"), where("fieldId", "==", userId));
        const unsubRes = onSnapshot(qRes, (snapshot) => {
            setReservations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const fetchFacility = async () => {
            try {
                const docSnap = await getDoc(doc(db, "tesisler", userId));
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFacility(prev => ({ 
                        ...prev, 
                        ...data,
                        name: data.fieldName || data.name || prev.name,
                        ownerFirstName: data.ownerFirstName || (data.name !== data.fieldName ? data.name : "") || prev.ownerFirstName,
                        ownerLastName: data.ownerLastName || data.surname || prev.ownerLastName
                    }));
                }
            } catch (err) { console.error("Fetch facility error:", err); }
        };

        fetchFacility();
        setDateList(generateDateList());

        // Maç kayıtlarını çek
        const qKayit = query(collection(db, "macKayitlari"), where("fieldId", "==", userId), orderBy("createdAt", "desc"));
        const unsubKayit = onSnapshot(qKayit, (snap) => {
            setMacKayitlari(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, () => {});

        return () => { unsubRes(); unsubKayit(); };
    }, [userId, navigate]);

    const handleSaveFacility = async (e) => {
        e.preventDefault();
        try {
            const facilityRef = doc(db, "tesisler", userId);
            await setDoc(facilityRef, {
                ...facility,
                fieldName: facility.name || facility.fieldName, // Eski ve yeni sistemle tam uyum
                name: facility.name || facility.fieldName,
                ownerFirstName: facility.ownerFirstName || "",
                ownerLastName: facility.ownerLastName || "",
                ownerId: userId,
                status: "approved", // Tesis güncellendiğinde ve yayınlandığında doğrudan onaylı (approved) olarak yayına girer
                iban: deleteField(),
                accountHolder: deleteField(),
                updatedAt: serverTimestamp()
            }, { merge: true });
            alert("Bilgiler Başarıyla Güncellendi ve Saha Yayına Alındı!");
        } catch (err) { alert("Hata: " + err.message); }
    };

    const handleManualAdd = async (e) => {
        e.preventDefault();
        if (reservations.some(r => r.date === selectedDate && r.timeSlot === selectedTime)) {
            alert("Bu saat zaten dolu!");
            return;
        }
        try {
            await addDoc(collection(db, "rezervasyonlar"), {
                fieldId: userId,
                customerName: `${customerName} (${customerPhone})`,
                date: selectedDate,
                timeSlot: selectedTime,
                status: 'blocked',
                type: 'blocked',
                createdAt: serverTimestamp()
            });
            alert("Saat başarıyla bloklandı.");
            setCustomerName(""); setCustomerPhone("");
        } catch (err) { alert("Hata oluştu!"); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bu rezervasyonu tamamen silmek istiyor musunuz?")) {
            try {
                await deleteDoc(doc(db, "rezervasyonlar", id));
            } catch (err) { alert("Hata oluştu!"); }
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        const confirmMsg = newStatus === 'confirmed' ? "Ödemeyi onaylıyor musunuz? Kullanıcıya onay SMS'i gidecektir." : "Ödemeyi reddediyor musunuz? Bu durum SuperAdmin'e bildirilecektir.";
        if (window.confirm(confirmMsg)) {
            try {
                const resRef = doc(db, "rezervasyonlar", id);
                await setDoc(resRef, { status: newStatus, updatedAt: serverTimestamp() }, { merge: true });

                if (newStatus === 'confirmed') {
                    // Phase 5A: Halı sahacı onayladı -> Kullanıcıya SMS
                    console.log("SMS GÖNDERİLİYOR (Kullanıcı): Randevunuz onaylanmıştır!");
                } else if (newStatus === 'admin_rejected') {
                    // Phase 5B: Halı sahacı reddetti -> SuperAdmin'e Mail
                    console.log("MAİL GÖNDERİLİYOR (SuperAdmin): DİKKAT: Bir rezervasyon reddedildi!");
                }

                alert("İşlem tamamlandı.");
            } catch (err) { alert("Hata oluştu!"); }
        }
    };

    const handleImageUpload = () => {
        const widget = window.cloudinary.createUploadWidget(
            { 
                cloudName: 'dpx6vshoa', 
                uploadPreset: 'sahalive_preset',
                sources: ['local'], // Sadece yerel dosyalar (direkt galeri/dosya seçici)
                multiple: false,
                clientAllowedFormats: ["png", "jpg", "jpeg", "webp"],
                maxFileSize: 5000000,
                styles: {
                    palette: {
                        window: "#FFFFFF",
                        windowBorder: "#90A0B3",
                        tabIcon: "#0078FF",
                        menuIcons: "#5A616A",
                        textDark: "#000000",
                        textLight: "#FFFFFF",
                        link: "#0078FF",
                        action: "#FF620C",
                        inactiveTabIcon: "#0E2F5A",
                        error: "#F44235",
                        inProgress: "#0078FF",
                        complete: "#20B832",
                        sourceBg: "#E4EBF1"
                    }
                }
            },
            (error, result) => {
                if (!error && result && result.event === "success") {
                    setFacility(prev => ({ ...prev, imageUrl: result.info.secure_url }));
                    alert("Fotoğraf başarıyla yüklendi!");
                }
            }
        );
        widget.open();
    };

    const onlineReservations = reservations.filter(r => r.type === 'online');
    const depositPerRes = facility.depositAmount || 1500;
    const totalEarnings = onlineReservations.length * depositPerRes;
    const totalDept = onlineReservations.length * 50;

    // --- CANLI YAYIN FONKSIYONLARI ---
    const handleSaveLiveUrl = async () => {
        try {
            await setDoc(doc(db, "tesisler", userId), { liveStreamUrl: liveUrl, updatedAt: serverTimestamp() }, { merge: true });
            alert("Canlı yayın adresi kaydedildi!");
        } catch (err) { alert("Hata: " + err.message); }
    };

    const handleTestLive = () => {
        setLiveTestMode(true);
        setLiveLoaded(false);
        setTimeout(() => {
            const scriptId = 'hls-cdn-admin';
            let script = document.getElementById(scriptId);
            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
                script.async = true;
                script.onload = () => setLiveLoaded(true);
                document.body.appendChild(script);
            } else {
                setLiveLoaded(true);
            }
        }, 100);
    };

    useEffect(() => {
        if (!liveLoaded || !liveTestMode || !videoTestRef.current || !liveUrl) return;
        const video = videoTestRef.current;
        if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = liveUrl;
            video.play().catch(() => {});
        } else if (window.Hls && window.Hls.isSupported()) {
            const hls = new window.Hls({ lowLatencyMode: true });
            hlsRef.current = hls;
            hls.loadSource(liveUrl);
            hls.attachMedia(video);
            hls.on(window.Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
        }
        return () => { if (hlsRef.current) { hlsRef.current.destroy(); } };
    }, [liveLoaded, liveTestMode, liveUrl]);

    // --- MAÇ KAYIT FONKSIYONLARI ---
    const handleVideoUpload = () => {
        const widget = window.cloudinary.createUploadWidget(
            { cloudName: 'dpx6vshoa', uploadPreset: 'sahalive_preset', sources: ['local'], multiple: false, clientAllowedFormats: ['mp4', 'mov', 'avi', 'mkv', 'webm'], maxFileSize: 500000000 },
            (error, result) => {
                if (!error && result && result.event === "success") {
                    setYeniKayit(prev => ({ ...prev, videoUrl: result.info.secure_url }));
                    alert("Video yüklendi!");
                }
            }
        );
        widget.open();
    };

    const handleSaveKayit = async (e) => {
        e.preventDefault();
        if (!yeniKayit.videoUrl) { alert("Lütfen önce video yükleyin!"); return; }
        try {
            await addDoc(collection(db, "macKayitlari"), {
                ...yeniKayit,
                fieldId: userId,
                fieldName: facility.name || facility.fieldName || "",
                city: facility.city || "",
                district: facility.district || "",
                createdAt: serverTimestamp()
            });
            setYeniKayit({ date: "", timeSlot: "", score: "", duration: "60 dk", videoUrl: "" });
            alert("Maç kaydı eklendi!");
        } catch (err) { alert("Hata: " + err.message); }
    };

    const handleDeleteKayit = async (id) => {
        if (window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) {
            await deleteDoc(doc(db, "macKayitlari", id));
        }
    };

    // Finansal istatistikleri günlük, aylık ve yıllık olarak hesapla
    const getFinancialStats = () => {
        const stats = {
            daily: {},
            monthly: {},
            yearly: {}
        };

        onlineReservations.forEach(res => {
            if (!res.date) return;
            const dateStr = res.date;
            const monthStr = dateStr.substring(0, 7); // YYYY-MM
            const yearStr = dateStr.substring(0, 4);  // YYYY

            const deposit = Number(facility.depositAmount || 1500);
            const platformFee = 50;

            // Günlük
            if (!stats.daily[dateStr]) {
                stats.daily[dateStr] = { count: 0, fieldEarnings: 0, platformFees: 0 };
            }
            stats.daily[dateStr].count += 1;
            stats.daily[dateStr].fieldEarnings += deposit;
            stats.daily[dateStr].platformFees += platformFee;

            // Aylık
            if (!stats.monthly[monthStr]) {
                stats.monthly[monthStr] = { count: 0, fieldEarnings: 0, platformFees: 0 };
            }
            stats.monthly[monthStr].count += 1;
            stats.monthly[monthStr].fieldEarnings += deposit;
            stats.monthly[monthStr].platformFees += platformFee;

            // Yıllık
            if (!stats.yearly[yearStr]) {
                stats.yearly[yearStr] = { count: 0, fieldEarnings: 0, platformFees: 0 };
            }
            stats.yearly[yearStr].count += 1;
            stats.yearly[yearStr].fieldEarnings += deposit;
            stats.yearly[yearStr].platformFees += platformFee;
        });

        return stats;
    };

    const finStats = getFinancialStats();
    const timeSlots = generateDynamicHours(facility);

    return (
        <div className="container-fluid p-0" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
            <div className="row g-0">
                {/* SOL MENÜ (Sidebar) */}
                <div className="col-md-3 col-lg-2 bg-dark text-white p-4" style={{ minHeight: '100vh' }}>
                    <div className="text-center mb-5">
                        <Logo width="180px" lightText={true} />
                        <div className="mt-1"><small className="text-muted">Panel v2.0</small></div>
                    </div>
                    <nav className="nav flex-column gap-2">
                        <button className={`btn w-100 text-start ${activeTab === 'ayarlar' ? 'btn-success' : 'btn-outline-light border-0'}`} onClick={() => setActiveTab('ayarlar')}>
                            <i className="fas fa-cog me-2"></i> Tesis Ayarları
                        </button>
                        <button className={`btn w-100 text-start ${activeTab === 'randevular' ? 'btn-success' : 'btn-outline-light border-0'}`} onClick={() => setActiveTab('randevular')}>
                            <i className="fas fa-calendar-alt me-2"></i> Randevular
                        </button>
                        <button className={`btn w-100 text-start ${activeTab === 'yayin' ? 'btn-success' : 'btn-outline-light border-0'}`} onClick={() => setActiveTab('yayin')}>
                            <i className="fas fa-video me-2"></i> Canlı Yayın
                        </button>
                        <button className={`btn w-100 text-start ${activeTab === 'kayitlar' ? 'btn-success' : 'btn-outline-light border-0'}`} onClick={() => setActiveTab('kayitlar')}>
                            <i className="fas fa-film me-2"></i> Maç Kayıtları
                        </button>
                        <button className={`btn w-100 text-start ${activeTab === 'finans' ? 'btn-success' : 'btn-outline-light border-0'}`} onClick={() => setActiveTab('finans')}>
                            <i className="fas fa-wallet me-2"></i> Finans Faktör
                        </button>
                        <hr />
                        <button className="btn btn-outline-danger border-0 text-start" onClick={() => { localStorage.clear(); navigate('/'); }}>
                            <i className="fas fa-sign-out-alt me-2"></i> Çıkış
                        </button>
                    </nav>
                </div>

                {/* ANA İÇERİK */}
                <div className="col-md-9 col-lg-10 p-4 p-md-5">

                    {/* ÜST BİLGİ KARTLARI */}
                    <div className="row g-3 mb-5">
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                                <small className="text-muted fw-bold">TOPLAM KAZANÇ</small>
                                <h3 className="fw-bold text-dark m-0">{totalEarnings.toLocaleString()} ₺</h3>
                                <small className="text-success mt-2"><i className="fas fa-arrow-up me-1"></i>Beklenen Kapora</small>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white border-start border-danger border-4">
                                <small className="text-muted fw-bold">SİSTEM BORCU</small>
                                <h3 className="fw-bold text-danger m-0">{totalDept.toLocaleString()} ₺</h3>
                                <small className="text-muted mt-2">Rezervasyon başı 50 ₺</small>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm rounded-4 p-4 bg-success text-white">
                                <small className="opacity-75 fw-bold">NET DURUM</small>
                                <h3 className="fw-bold m-0">{(totalEarnings - totalDept).toLocaleString()} ₺</h3>
                                <small className="opacity-75 mt-2">Ödemeler Alınınca</small>
                            </div>
                        </div>
                    </div>

                    {/* TAB İÇERİKLERİ */}
                    {activeTab === 'ayarlar' && (
                        <div className="animate__animated animate__fadeIn">
                            <div className="card border-0 shadow rounded-4 p-4 bg-white">
                                <h4 className="fw-bold mb-4 text-dark"><i className="fas fa-edit text-success me-2"></i>Saha ve Tesis Ayarları</h4>
                                <form onSubmit={handleSaveFacility}>
                                    <div className="row g-4">
                                        {/* TEMEL BİLGİLER */}
                                        <div className="col-12 border-bottom pb-2"><h6 className="fw-bold m-0 text-muted small">TEMEL BİLGİLER</h6></div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold small">Halı Saha Adı</label>
                                            <input type="text" className="form-control" value={facility.name} onChange={e => setFacility({ ...facility, name: e.target.value })} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold small text-primary">Saha Kapora Tutarı (₺)</label>
                                            <input type="number" className="form-control border-primary" value={facility.depositAmount} onChange={e => setFacility({ ...facility, depositAmount: Number(e.target.value) })} required />
                                            <small className="text-muted">Müşteri {(facility.depositAmount || 1500) + 50} ₺ ödeyecektir.</small>
                                        </div>
                                        <div className="col-12 mt-2">
                                            <label className="form-label fw-bold small text-danger"><i className="fas fa-video me-1"></i> Canlı Yayın Akış Adresi (HLS .m3u8 URL)</label>
                                            <input 
                                                type="url" 
                                                className="form-control" 
                                                placeholder="Örn: http://localhost:8888/sahalive/index.m3u8" 
                                                value={facility.liveStreamUrl || ""} 
                                                onChange={e => setFacility({ ...facility, liveStreamUrl: e.target.value })} 
                                            />
                                            <small className="text-muted">Kameranızdan gelen RTSP akışını MediaMTX/go2rtc ile HLS'e dönüştürdükten sonra elde ettiğiniz .m3u8 uzantılı adresi girin.</small>
                                        </div>

                                        {/* LOKASYON & SAATLER */}
                                        <div className="col-12 border-bottom pb-2 mt-4"><h6 className="fw-bold m-0 text-muted small">LOKASYON & ÇALIŞMA SAATLERİ</h6></div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold small">Şehir</label>
                                            <select 
                                                className="form-select" 
                                                value={facility.city} 
                                                onChange={e => setFacility({ ...facility, city: e.target.value, district: "" })} 
                                                required
                                            >
                                                <option value="">Şehir Seçin</option>
                                                {Object.keys(turkeyCities).sort().map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold small">İlçe</label>
                                            <select 
                                                className="form-select" 
                                                value={facility.district} 
                                                onChange={e => setFacility({ ...facility, district: e.target.value })} 
                                                disabled={!facility.city}
                                                required
                                            >
                                                <option value="">{facility.city ? "İlçe Seçin" : "Önce Şehir Seçin"}</option>
                                                {facility.city && turkeyCities[facility.city].sort().map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold small">Açılış</label>
                                            <input type="time" className="form-control" value={facility.openTime} onChange={e => setFacility({ ...facility, openTime: e.target.value })} required />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold small">Kapanış</label>
                                            <input type="time" className="form-control" value={facility.closeTime} onChange={e => setFacility({ ...facility, closeTime: e.target.value })} required />
                                        </div>

                                        {/* SAHİBİ & ÖDEME */}
                                        <div className="col-12 border-bottom pb-2 mt-4"><h6 className="fw-bold m-0 text-muted small">YETKİLİ & ÖDEME BİLGİLERİ</h6></div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold small">Adınız</label>
                                            <input type="text" className="form-control" value={facility.ownerFirstName} onChange={e => setFacility({ ...facility, ownerFirstName: e.target.value })} required />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold small">Soyadınız</label>
                                            <input type="text" className="form-control" value={facility.ownerLastName} onChange={e => setFacility({ ...facility, ownerLastName: e.target.value })} required />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold small">Telefon</label>
                                            <input type="tel" className="form-control" value={facility.phone} onChange={e => setFacility({ ...facility, phone: e.target.value })} required />
                                        </div>

                                        {/* İMKANLAR */}
                                        <div className="col-12 border-bottom pb-2 mt-4"><h6 className="fw-bold m-0 text-muted small">TESİS İMKANLARI</h6></div>
                                        <div className="col-12">
                                            <div className="d-flex flex-wrap gap-2">
                                                {[
                                                    { key: 'dus', label: 'Duş', icon: '🚿' },
                                                    { key: 'otopark', label: 'Otopark', icon: '🅿️' },
                                                    { key: 'kantin', label: 'Kantin', icon: '☕' },
                                                    { key: 'krampon', label: 'Krampon', icon: '👟' },
                                                    { key: 'eldiven', label: 'Eldiven', icon: '🧤' },
                                                    { key: 'servis', label: 'Servis', icon: '🚐' }
                                                ].map(item => (
                                                    <div key={item.key} 
                                                        onClick={() => setFacility(prev => ({ ...prev, amenities: { ...prev.amenities, [item.key]: !prev.amenities[item.key] } }))}
                                                        className={`p-2 rounded-3 border d-flex align-items-center gap-2 ${facility.amenities?.[item.key] ? 'border-success bg-success bg-opacity-10 text-success' : 'bg-light text-muted opacity-50'}`}
                                                        style={{ cursor: 'pointer', flex: '1 1 120px', transition: '0.2s' }}
                                                    >
                                                        <span>{item.icon}</span>
                                                        <span className="small fw-bold">{item.label}</span>
                                                        {facility.amenities?.[item.key] && <i className="fas fa-check-circle ms-auto"></i>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* FOTOĞRAF */}
                                        <div className="col-12 mt-4 text-center">
                                            <div className="mb-3 border rounded-4 p-3 bg-light d-inline-block shadow-sm">
                                                {facility.imageUrl ? (
                                                    <img src={facility.imageUrl} alt="Saha" style={{ width: '280px', height: '160px', objectFit: 'cover', borderRadius: '15px' }} />
                                                ) : <div className="text-muted small py-4 px-5">Saha Fotoğrafı Yüklenmemiş</div>}
                                            </div>
                                            <br />
                                            <button type="button" className="btn btn-outline-dark btn-sm rounded-pill px-4 mb-3" onClick={handleImageUpload}>
                                                <i className="fas fa-camera me-2"></i> Saha Fotoğrafı Yükle
                                            </button>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-dark w-100 py-3 rounded-4 fw-bold shadow-sm mt-4">BİLGİLERİ GÜNCELLE VE YAYINLA</button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'randevular' && (
                        <div className="animate__animated animate__fadeIn">
                            <div className="row g-4">
                                <div className="col-lg-4">
                                    <div className="card border-0 shadow rounded-4 p-4 bg-white">
                                        <h5 className="fw-bold mb-4"><i className="fas fa-user-lock text-success me-2"></i>Saha Saati Blokla</h5>
                                        <form onSubmit={handleManualAdd}>
                                            <div className="mb-3">
                                                <input type="text" className="form-control bg-light border-0" placeholder="Müşteri Ad Soyad" value={customerName} onChange={e => setCustomerName(e.target.value)} required />
                                            </div>
                                            <div className="mb-3">
                                                <input type="tel" className="form-control bg-light border-0" placeholder="Telefon" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} required />
                                            </div>
                                            <div className="mb-3">
                                                <label className="small fw-bold mb-2">Tarih Seçin</label>
                                                <div className="d-flex gap-2 overflow-auto pb-2 scrollbar-hidden" style={{ whiteSpace: 'nowrap' }}>
                                                    {dateList.map(date => (
                                                        <button
                                                            key={date.value}
                                                            type="button"
                                                            className={`btn btn-sm rounded-pill px-3 ${selectedDate === date.value ? 'btn-success' : 'btn-outline-dark'}`}
                                                            onClick={() => setSelectedDate(date.value)}
                                                        >
                                                            {date.fullDate.split(' ')[0]} {date.fullDate.split(' ')[1].substring(0, 3)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className="small fw-bold mb-2">Saat Seçin</label>
                                                <select className="form-select bg-light border-0" value={selectedTime} onChange={e => setSelectedTime(e.target.value)} required>
                                                    <option value="">Saat Seçin</option>
                                                    {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <button type="submit" className="btn btn-dark w-100 py-3 rounded-4 fw-bold">SAATİ KAPAT</button>
                                        </form>
                                    </div>
                                </div>
                                <div className="col-lg-8">
                                    <div className="card border-0 shadow rounded-4 p-0 bg-white overflow-hidden">
                                        <div className="p-4 bg-dark text-white d-flex justify-content-between">
                                            <h5 className="m-0 fw-bold">Pasif/Dolu Saatler</h5>
                                            <span className="badge bg-success">{reservations.length} Kayıt</span>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-hover m-0 align-middle text-center">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Tarih/Saat</th>
                                                        <th>Müşteri</th>
                                                        <th>Tür / Ödeme</th>
                                                        <th>Durum</th>
                                                        <th className="text-end pe-4">İşlem</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reservations.sort((a, b) => b.createdAt - a.createdAt).map(res => (
                                                        <tr key={res.id}>
                                                            <td className="ps-4 py-3">
                                                                <div className="fw-bold text-dark">{res.date}</div>
                                                                <small className="text-muted">{res.timeSlot}</small>
                                                            </td>
                                                            <td className="fw-bold">
                                                                {res.customerName}
                                                                {res.type === 'online' && <div className="extra-small text-primary"><i className="fas fa-credit-card me-1"></i>Kapora Ödendi</div>}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <span className={`badge mb-1 ${res.type === 'blocked' ? 'bg-secondary' : 'bg-primary'}`}>
                                                                        {res.type === 'blocked' ? 'BLOKLANDI' : 'ONLINE'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {res.status === 'pending_approval' ? <span className="badge bg-warning text-dark border border-warning shadow-sm"><i className="fas fa-clock me-1"></i>Onay Bekliyor</span> :
                                                                    res.status === 'confirmed' ? <span className="badge bg-success"><i className="fas fa-check me-1"></i>Onaylandı</span> :
                                                                        res.status === 'rejected' ? <span className="badge bg-danger"><i className="fas fa-times me-1"></i>Reddedildi</span> :
                                                                            res.status === 'admin_rejected' ? <span className="badge bg-danger">Sahacı Reddetti</span> :
                                                                                <span className="badge bg-info">{res.status}</span>}
                                                            </td>
                                                            <td className="text-end pe-4">
                                                                {res.status === 'pending_approval' ? (
                                                                    <div className="d-flex gap-1 justify-content-end">
                                                                        <button className="btn btn-sm btn-success shadow-sm" onClick={() => handleUpdateStatus(res.id, 'confirmed')} title="Onayla"><i className="fas fa-check me-1"></i>Onayla</button>
                                                                        <button className="btn btn-sm btn-danger shadow-sm" onClick={() => handleUpdateStatus(res.id, 'admin_rejected')} title="Reddet"><i className="fas fa-times"></i></button>
                                                                    </div>
                                                                ) : (
                                                                    <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDelete(res.id)}><i className="fas fa-trash"></i></button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'yayin' && (
                        <div className="animate__animated animate__fadeIn">
                            <div className="card border-0 shadow rounded-4 p-4 bg-white mb-4">
                                <h4 className="fw-bold mb-1 text-dark"><i className="fas fa-video text-danger me-2"></i>Canlı Yayın Yönetimi</h4>
                                <p className="text-muted small mb-4">Kameranızdan gelen RTSP akışını go2rtc yazılımı ile HLS formatına dönüştürmeniz gerekir. Elde ettiğiniz <code>.m3u8</code> adresini buraya girin.</p>

                                {/* Adım adım kurulum kılavuzu */}
                                <div className="alert alert-dark border-0 rounded-4 mb-4">
                                    <h6 className="fw-bold mb-3"><i className="fas fa-terminal me-2 text-success"></i>Kurulum Adımları</h6>
                                    <ol className="mb-0 ps-3" style={{lineHeight: '2'}}>
                                        <li><strong>go2rtc.exe</strong>'yi indirin ve saha bilgisayarına kurun.</li>
                                        <li><code>go2rtc.yaml</code> dosyasına kamera RTSP URL'nizi girin:<br/>
                                            <code className="bg-black text-success px-2 py-1 rounded d-inline-block mt-1">- rtsp://KULLANICI:SIFRE@KAMERA_IP:554/stream1</code>
                                        </li>
                                        <li>go2rtc'yi başlatın. Tarayıcıdan <code>http://localhost:1984</code> adresine girin.</li>
                                        <li>Akış adınızı (örn: <code>tapo_cam</code>) kullanarak HLS URL'nizi oluşturun:<br/>
                                            <code className="bg-black text-warning px-2 py-1 rounded d-inline-block mt-1">http://BILGISAYAR_IP:1984/api/streams/tapo_cam/master.m3u8</code>
                                        </li>
                                        <li>Bu adresi aşağıdaki alana yapıştırın ve kaydedin.</li>
                                    </ol>
                                </div>

                                <div className="row g-3 align-items-end">
                                    <div className="col-md-8">
                                        <label className="form-label fw-bold">HLS Yayın Adresi (.m3u8)</label>
                                        <input
                                            type="url"
                                            className="form-control form-control-lg border-2"
                                            placeholder="http://192.168.1.100:1984/api/streams/kamera/master.m3u8"
                                            value={liveUrl}
                                            onChange={e => setLiveUrl(e.target.value)}
                                        />
                                        <small className="text-muted">Saha ağı dışından erişilebilir bir IP/domain olmalıdır.</small>
                                    </div>
                                    <div className="col-md-2">
                                        <button className="btn btn-outline-dark w-100 py-3" onClick={handleTestLive} disabled={!liveUrl}>
                                            <i className="fas fa-play me-1"></i> Test Et
                                        </button>
                                    </div>
                                    <div className="col-md-2">
                                        <button className="btn btn-success w-100 py-3 fw-bold" onClick={handleSaveLiveUrl} disabled={!liveUrl}>
                                            <i className="fas fa-save me-1"></i> Kaydet
                                        </button>
                                    </div>
                                </div>

                                {liveTestMode && liveUrl && (
                                    <div className="mt-4">
                                        <h6 className="fw-bold mb-2">Yayın Önizleme</h6>
                                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000', borderRadius: '12px', overflow: 'hidden' }}>
                                            <video
                                                ref={videoTestRef}
                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                controls muted playsInline
                                                style={{ objectFit: 'contain' }}
                                            />
                                        </div>
                                        <small className="text-muted">Bağlanamıyorsa go2rtc'nin çalıştığından ve IP adresinin doğru olduğundan emin olun.</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'kayitlar' && (
                        <div className="animate__animated animate__fadeIn">
                            <div className="row g-4">
                                {/* Yeni Kayıt Ekle */}
                                <div className="col-lg-5">
                                    <div className="card border-0 shadow rounded-4 p-4 bg-white">
                                        <h5 className="fw-bold mb-4"><i className="fas fa-plus-circle text-success me-2"></i>Yeni Maç Kaydı Ekle</h5>
                                        <form onSubmit={handleSaveKayit}>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold small">Maç Tarihi</label>
                                                <input type="date" className="form-control" value={yeniKayit.date} onChange={e => setYeniKayit({...yeniKayit, date: e.target.value})} required />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold small">Saat Aralığı</label>
                                                <input type="text" className="form-control" placeholder="Örn: 20:00 - 21:00" value={yeniKayit.timeSlot} onChange={e => setYeniKayit({...yeniKayit, timeSlot: e.target.value})} required />
                                            </div>
                                            <div className="row g-2 mb-3">
                                                <div className="col">
                                                    <label className="form-label fw-bold small">Skor</label>
                                                    <input type="text" className="form-control" placeholder="Örn: 4-3" value={yeniKayit.score} onChange={e => setYeniKayit({...yeniKayit, score: e.target.value})} />
                                                </div>
                                                <div className="col">
                                                    <label className="form-label fw-bold small">Süre</label>
                                                    <input type="text" className="form-control" placeholder="60 dk" value={yeniKayit.duration} onChange={e => setYeniKayit({...yeniKayit, duration: e.target.value})} />
                                                </div>
                                            </div>
                                            <div className="mb-4">
                                                <label className="form-label fw-bold small">Video Dosyası</label>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <button type="button" className="btn btn-outline-dark rounded-pill px-4" onClick={handleVideoUpload}>
                                                        <i className="fas fa-upload me-2"></i>Video Yükle
                                                    </button>
                                                    {yeniKayit.videoUrl && <span className="badge bg-success"><i className="fas fa-check me-1"></i>Video Hazır</span>}
                                                </div>
                                                <small className="text-muted d-block mt-1">MP4, MOV, AVI formatları desteklenir. Max 500MB.</small>
                                            </div>
                                            <button type="submit" className="btn btn-success w-100 py-3 fw-bold rounded-4" disabled={!yeniKayit.videoUrl}>
                                                <i className="fas fa-film me-2"></i>Kayıtı Sisteme Ekle
                                            </button>
                                        </form>
                                    </div>
                                </div>

                                {/* Mevcut Kayıtlar */}
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow rounded-4 p-0 bg-white overflow-hidden">
                                        <div className="p-4 bg-dark text-white d-flex justify-content-between">
                                            <h5 className="m-0 fw-bold"><i className="fas fa-film me-2"></i>Mevcut Kayıtlar</h5>
                                            <span className="badge bg-success">{macKayitlari.length} Kayıt</span>
                                        </div>
                                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            {macKayitlari.length === 0 ? (
                                                <div className="text-center py-5 text-muted">
                                                    <i className="fas fa-film fa-3x opacity-25 mb-3"></i>
                                                    <p>Henüz maç kaydı eklenmemiş.</p>
                                                </div>
                                            ) : macKayitlari.map(kayit => (
                                                <div key={kayit.id} className="p-3 border-bottom d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="fw-bold text-dark">{kayit.date} | {kayit.timeSlot}</div>
                                                        <small className="text-muted">{kayit.duration} {kayit.score && <span className="badge bg-danger ms-1">{kayit.score}</span>}</small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <a href={kayit.videoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-dark"><i className="fas fa-play"></i></a>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteKayit(kayit.id)}><i className="fas fa-trash"></i></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'finans' && (
                        <div className="animate__animated animate__fadeIn">
                            <div className="card border-0 shadow rounded-4 p-4 bg-white mb-4">
                                <h4 className="fw-bold mb-3 text-dark"><i className="fas fa-wallet text-success me-2"></i>Finans Faktör Raporu</h4>
                                <p className="text-muted small">Bu ekrandan müşterilerden toplanan kapora ödemelerini, platform hizmet bedeli kesintilerini ve net kazançlarınızı inceleyebilirsiniz.</p>
                                
                                <div className="row g-3 my-2 text-center">
                                    <div className="col-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <small className="text-muted fw-bold d-block">ONLINE REZERVASYON</small>
                                            <h4 className="fw-bold text-dark m-0 mt-1">{onlineReservations.length} Adet</h4>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <small className="text-muted fw-bold d-block">BRÜT KAPORA KAZANCI</small>
                                            <h4 className="fw-bold text-primary m-0 mt-1">{(totalEarnings + totalDept).toLocaleString()} ₺</h4>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <small className="text-muted fw-bold d-block">KOMİSYON (Platform Payı)</small>
                                            <h4 className="fw-bold text-danger m-0 mt-1">{totalDept.toLocaleString()} ₺</h4>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="p-3 bg-success bg-opacity-10 rounded-3 border border-success">
                                            <small className="text-success fw-bold d-block">NET HAK EDİŞİNİZ</small>
                                            <h4 className="fw-bold text-success m-0 mt-1">{totalEarnings.toLocaleString()} ₺</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Finansal Grafik ve Tablo Görünümü */}
                            <div className="card border-0 shadow rounded-4 p-4 bg-white">
                                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                    <h5 className="fw-bold m-0 text-dark"><i className="fas fa-chart-line me-2"></i>Kazanç Dönem Analizleri</h5>
                                    
                                    <div className="btn-group btn-group-sm border rounded-pill p-1 bg-light">
                                        <button className={`btn rounded-pill px-3 fw-bold ${finPeriod === 'gun' ? 'btn-success text-white' : 'btn-light'}`} onClick={() => setFinPeriod('gun')}>Günlük</button>
                                        <button className={`btn rounded-pill px-3 fw-bold ${finPeriod === 'ay' ? 'btn-success text-white' : 'btn-light'}`} onClick={() => setFinPeriod('ay')}>Aylık</button>
                                        <button className={`btn rounded-pill px-3 fw-bold ${finPeriod === 'yil' ? 'btn-success text-white' : 'btn-light'}`} onClick={() => setFinPeriod('yil')}>Yıllık</button>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-hover align-middle text-center border">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Dönem / Tarih</th>
                                                <th>Rezervasyon Sayısı</th>
                                                <th>Müşteriden Toplanan Brüt</th>
                                                <th>SahaLive Komisyonu (50 ₺/Rez)</th>
                                                <th>Sizin Net Kazancınız</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {finPeriod === 'gun' && (
                                                Object.keys(finStats.daily).length > 0 ? (
                                                    Object.keys(finStats.daily).sort((a,b) => b.localeCompare(a)).map(date => {
                                                        const row = finStats.daily[date];
                                                        return (
                                                            <tr key={date}>
                                                                <td className="fw-bold text-primary">{date}</td>
                                                                <td>{row.count} Adet</td>
                                                                <td className="text-success fw-bold">{(row.fieldEarnings + row.platformFees).toLocaleString()} ₺</td>
                                                                <td className="text-danger fw-bold">{row.platformFees.toLocaleString()} ₺</td>
                                                                <td className="text-success fw-bold bg-light">{row.fieldEarnings.toLocaleString()} ₺</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : <tr><td colSpan="5" className="py-4 text-muted">Kayıtlı günlük finansal veri bulunamadı.</td></tr>
                                            )}
                                            {finPeriod === 'ay' && (
                                                Object.keys(finStats.monthly).length > 0 ? (
                                                    Object.keys(finStats.monthly).sort((a,b) => b.localeCompare(a)).map(month => {
                                                        const row = finStats.monthly[month];
                                                        return (
                                                            <tr key={month}>
                                                                <td className="fw-bold text-primary">{month}</td>
                                                                <td>{row.count} Adet</td>
                                                                <td className="text-success fw-bold">{(row.fieldEarnings + row.platformFees).toLocaleString()} ₺</td>
                                                                <td className="text-danger fw-bold">{row.platformFees.toLocaleString()} ₺</td>
                                                                <td className="text-success fw-bold bg-light">{row.fieldEarnings.toLocaleString()} ₺</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : <tr><td colSpan="5" className="py-4 text-muted">Kayıtlı aylık finansal veri bulunamadı.</td></tr>
                                            )}
                                            {finPeriod === 'yil' && (
                                                Object.keys(finStats.yearly).length > 0 ? (
                                                    Object.keys(finStats.yearly).sort((a,b) => b.localeCompare(a)).map(year => {
                                                        const row = finStats.yearly[year];
                                                        return (
                                                            <tr key={year}>
                                                                <td className="fw-bold text-primary">{year}</td>
                                                                <td>{row.count} Adet</td>
                                                                <td className="text-success fw-bold">{(row.fieldEarnings + row.platformFees).toLocaleString()} ₺</td>
                                                                <td className="text-danger fw-bold">{row.platformFees.toLocaleString()} ₺</td>
                                                                <td className="text-success fw-bold bg-light">{row.fieldEarnings.toLocaleString()} ₺</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : <tr><td colSpan="5" className="py-4 text-muted">Kayıtlı yıllık finansal veri bulunamadı.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}

export default AdminDashboard;